import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import {
  AssetOcppCommandError,
  AssetService,
} from '~/app/services/asset/asset.service';
import type {
  AssetCommandConsoleEntry,
  AssetCommandRequest,
  AssetOcppCommandAction,
} from '../types';
import {
  assetCommandResultKey,
  buildInfraErrorConsoleEntry,
  buildSendConsoleEntries,
  buildSuccessConsoleEntries,
  parseHeartbeatIntervalFromConfiguration,
  requestPayloadFor,
} from '../utils';

type Options = {
  onConsoleEntries: (entries: readonly AssetCommandConsoleEntry[]) => void;
  onHeartbeatInterval?: (value: string | null) => void;
  /** Fired when GetDiagnostics creates / updates history (including timeouts). */
  onDiagnosticsRequested?: () => void;
};

/**
 * Shared command execution for the Commands tab.
 * One pending lock across all OCPP command forms.
 */
export function useAssetDetailCommandExecution(
  organizationId: string,
  assetId: string,
  options: Options,
) {
  const inFlightRef = useRef(false);

  const mutation = useMutation({
    mutationFn: async (request: AssetCommandRequest) => {
      const startedAt = Date.now();
      options.onConsoleEntries(
        buildSendConsoleEntries({
          action: request.action,
          requestPayload: requestPayloadFor(request),
        }),
      );
      try {
        const result = await AssetService.executeOcppCommand(
          organizationId,
          assetId,
          request,
        );
        options.onConsoleEntries(
          buildSuccessConsoleEntries({
            action: result.action as AssetOcppCommandAction,
            uniqueId: result.uniqueId,
            outcome: result.outcome,
            responsePayload: result.responsePayload,
            listVersion: result.listVersion,
            errorCode: result.errorCode,
            errorDescription: result.errorDescription,
            durationMs: Date.now() - startedAt,
          }),
        );

        if (
          result.action === 'GetConfiguration' &&
          result.outcome === 'accepted'
        ) {
          const heartbeat = parseHeartbeatIntervalFromConfiguration(
            result.responsePayload,
          );
          if (heartbeat != null) {
            options.onHeartbeatInterval?.(heartbeat);
          }
        }
        if (
          request.action === 'ChangeConfiguration' &&
          result.outcome === 'accepted'
        ) {
          options.onHeartbeatInterval?.(request.value);
        }
        if (request.action === 'GetDiagnostics') {
          options.onDiagnosticsRequested?.();
        }

        return result;
      } catch (error) {
        const status =
          error instanceof AssetOcppCommandError ? error.status : undefined;
        const reason =
          error instanceof AssetOcppCommandError ? error.reason : undefined;
        const key = assetCommandResultKey({ status, reason });
        const isTimeout = key === 'chargerTimeout' || key === 'ownerTimeout';
        options.onConsoleEntries([
          buildInfraErrorConsoleEntry({
            action: request.action,
            kind: isTimeout ? 'timeout' : 'infraError',
            durationMs: Date.now() - startedAt,
            messageKey: `detail.commands.${key}`,
          }),
        ]);
        if (request.action === 'GetDiagnostics') {
          // Row may already exist (REQUESTED) even when CALL times out.
          options.onDiagnosticsRequested?.();
        }
        throw error;
      }
    },
  });

  const activeAction: AssetOcppCommandAction | null = mutation.isPending
    ? (mutation.variables?.action ?? null)
    : null;

  const execute = async (request: AssetCommandRequest) => {
    if (inFlightRef.current || mutation.isPending) {
      return;
    }
    inFlightRef.current = true;
    try {
      await mutation.mutateAsync(request);
    } catch {
      // Console already recorded the failure; do not rethrow into Modal.confirm.
    } finally {
      inFlightRef.current = false;
    }
  };

  return {
    execute,
    isPending: mutation.isPending,
    activeAction,
  };
}
